import React, { useState, useRef, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import { useAuth } from './contexts/AuthContext';
import { userProfileAPI } from './services/api';
import Home from './components/Home';
import Login from './components/Login';
import GoogleCallback from './components/GoogleCallback';
import ProtectedRoute from './components/ProtectedRoute';
import CustomerList from './components/CustomerList';
import CustomerForm from './components/CustomerForm';
import PurchaseOrderList from './components/PurchaseOrderList';
import PurchaseOrderForm from './components/PurchaseOrderForm';
import PurchaseOrderDetail from './components/PurchaseOrderDetail';
import InvoiceList from './components/InvoiceList';
import InvoiceForm from './components/InvoiceForm';
import InvoiceDetail from './components/InvoiceDetail';
import ProjectList from './components/ProjectList';
import ProjectForm from './components/ProjectForm';
import BillingRateList from './components/BillingRateList';
import BillingRateForm from './components/BillingRateForm';
import UserRateManagement from './components/UserRateManagement';
import TimeEntry from './components/TimeEntry';
import RateGroupList from './components/RateGroupList';
import RateGroupForm from './components/RateGroupForm';
import TimesheetApprovalQueue from './components/TimesheetApprovalQueue';
import CustomerTimesheetReport from './components/CustomerTimesheetReport';
import UnbilledEntriesReport from './components/UnbilledEntriesReport';
import PayPeriodManagement from './components/PayPeriodManagement';
import BiweeklyTimesheet from './components/BiweeklyTimesheet';
import BiweeklyApprovalQueue from './components/BiweeklyApprovalQueue';
import BillingSettings from './components/BillingSettings';
import InternalTimeReport from './components/InternalTimeReport';
import MyTimesheets from './components/MyTimesheets';

// Navigation component to handle dropdowns and mobile menu
const Navigation: React.FC<{
  isAdmin: boolean;
  isHourly: boolean;
  user: { username?: string; role?: string } | null;
  logout: () => void;
}> = ({ isAdmin, isHourly, user, logout }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [billingOpen, setBillingOpen] = useState(false);
  const [timesheetsOpen, setTimesheetsOpen] = useState(false);
  const [ratesOpen, setRatesOpen] = useState(false);
  const location = useLocation();
  const navRef = useRef<HTMLElement>(null);

  const closeAllDropdowns = () => {
    setBillingOpen(false);
    setTimesheetsOpen(false);
    setRatesOpen(false);
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        closeAllDropdowns();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu and dropdowns on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    closeAllDropdowns();
  }, [location.pathname]);

  const toggleDropdown = (dropdown: 'billing' | 'timesheets' | 'rates') => {
    setBillingOpen(dropdown === 'billing' ? !billingOpen : false);
    setTimesheetsOpen(dropdown === 'timesheets' ? !timesheetsOpen : false);
    setRatesOpen(dropdown === 'rates' ? !ratesOpen : false);
  };

  // Check if any billing route is active
  const isBillingActive = ['/customers', '/projects', '/purchase-orders', '/invoices', '/unbilled-time', '/billing-settings'].some(
    path => location.pathname.startsWith(path)
  );

  // Check if any timesheets route is active
  const isTimesheetsActive = ['/pay-periods', '/timesheets/approvals', '/biweekly-approvals', '/timesheets/report', '/reports/time', '/my-timesheets'].some(
    path => location.pathname.startsWith(path)
  );

  // Check if any rates route is active
  const isRatesActive = ['/billing-rates', '/rate-groups', '/user-rates'].some(
    path => location.pathname.startsWith(path)
  );

  return (
    <nav className="navbar" ref={navRef}>
      <div className="navbar-content">
        <div className="navbar-left">
          <h1>Invoicing App</h1>
          <button
            className={`hamburger ${mobileMenuOpen ? 'open' : ''}`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>

        <div className={`navbar-menu ${mobileMenuOpen ? 'open' : ''}`}>
          <div className="navbar-links">
            <NavLink to="/" end>
              Home
            </NavLink>

            {isAdmin && (
              <>
                <div className="dropdown">
                  <button
                    className={`dropdown-toggle ${isBillingActive ? 'active' : ''}`}
                    onClick={() => toggleDropdown('billing')}
                  >
                    Billing <span className="dropdown-arrow">▼</span>
                  </button>
                  <div className={`dropdown-menu ${billingOpen ? 'open' : ''}`}>
                    <NavLink to="/customers">Companies</NavLink>
                    <NavLink to="/projects">Projects</NavLink>
                    <NavLink to="/purchase-orders">Purchase Orders</NavLink>
                    <NavLink to="/billing-settings">Billing Settings</NavLink>
                    <NavLink to="/invoices">Invoices</NavLink>
                    <NavLink to="/unbilled-time">Unbilled Time</NavLink>
                  </div>
                </div>

                <div className="dropdown">
                  <button
                    className={`dropdown-toggle ${isTimesheetsActive ? 'active' : ''}`}
                    onClick={() => toggleDropdown('timesheets')}
                  >
                    Timesheets <span className="dropdown-arrow">▼</span>
                  </button>
                  <div className={`dropdown-menu ${timesheetsOpen ? 'open' : ''}`}>
                    <NavLink to="/pay-periods">Pay Periods</NavLink>
                    <NavLink to="/timesheets/approvals">Approve Monthly</NavLink>
                    <NavLink to="/biweekly-approvals">Approve Biweekly</NavLink>
                    <NavLink to="/timesheets/report">Customer Timesheets</NavLink>
                    <NavLink to="/reports/time">Internal Timesheets</NavLink>
                  </div>
                </div>

                <div className="dropdown">
                  <button
                    className={`dropdown-toggle ${isRatesActive ? 'active' : ''}`}
                    onClick={() => toggleDropdown('rates')}
                  >
                    Rates <span className="dropdown-arrow">▼</span>
                  </button>
                  <div className={`dropdown-menu ${ratesOpen ? 'open' : ''}`}>
                    <NavLink to="/billing-rates">Rates</NavLink>
                    <NavLink to="/rate-groups">Rate Groups</NavLink>
                    <NavLink to="/user-rates">User Assignments</NavLink>
                  </div>
                </div>
              </>
            )}

            <NavLink to="/time-entry">Time Entry</NavLink>
            <NavLink to="/my-timesheets">My Timesheets</NavLink>
            {isHourly && <NavLink to="/biweekly">Biweekly</NavLink>}
          </div>

          <div className="navbar-right">
            <span className="navbar-user">
              {user?.username} ({user?.role?.replace('_', ' ')})
            </span>
            <button onClick={logout} className="btn btn-secondary navbar-logout">
              Logout
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
};

const App: React.FC = () => {
  const { isAuthenticated, user, logout, isAdmin } = useAuth();
  const [isHourly, setIsHourly] = useState(false);

  // Fetch user profile to check if they're hourly
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!isAuthenticated || !user?.id) {
        setIsHourly(false);
        return;
      }
      try {
        // Use the /me/ endpoint which is accessible to all authenticated users
        const response = await userProfileAPI.getMyProfile();
        setIsHourly(response.data.employee_type === 'hourly');
      } catch (err) {
        console.error('Failed to fetch user profile:', err);
        setIsHourly(false);
      }
    };
    fetchUserProfile();
  }, [isAuthenticated, user?.id]);

  return (
    <Router>
      <div className="App">
        {isAuthenticated && (
          <Navigation isAdmin={isAdmin} isHourly={isHourly} user={user} logout={logout} />
        )}

        <Routes>
          <Route path="/login" element={isAuthenticated ? <Navigate to="/" /> : <Login />} />
          <Route path="/auth/google/callback" element={<GoogleCallback />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Home />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customers"
            element={
              <ProtectedRoute requireAdmin>
                <CustomerList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customers/new"
            element={
              <ProtectedRoute requireAdmin>
                <CustomerForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customers/:id/edit"
            element={
              <ProtectedRoute requireAdmin>
                <CustomerForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/purchase-orders"
            element={
              <ProtectedRoute requireAdmin>
                <PurchaseOrderList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/purchase-orders/new"
            element={
              <ProtectedRoute requireAdmin>
                <PurchaseOrderForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/purchase-orders/:id"
            element={
              <ProtectedRoute requireAdmin>
                <PurchaseOrderDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/purchase-orders/:id/edit"
            element={
              <ProtectedRoute requireAdmin>
                <PurchaseOrderForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/invoices"
            element={
              <ProtectedRoute requireAdmin>
                <InvoiceList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/invoices/new"
            element={
              <ProtectedRoute requireAdmin>
                <InvoiceForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/invoices/:id"
            element={
              <ProtectedRoute requireAdmin>
                <InvoiceDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/invoices/:id/edit"
            element={
              <ProtectedRoute requireAdmin>
                <InvoiceForm />
              </ProtectedRoute>
            }
          />

          <Route
            path="/projects"
            element={
              <ProtectedRoute requireAdmin>
                <ProjectList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/new"
            element={
              <ProtectedRoute requireAdmin>
                <ProjectForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/:id/edit"
            element={
              <ProtectedRoute requireAdmin>
                <ProjectForm />
              </ProtectedRoute>
            }
          />

          <Route
            path="/billing-rates"
            element={
              <ProtectedRoute requireAdmin>
                <BillingRateList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/billing-rates/new"
            element={
              <ProtectedRoute requireAdmin>
                <BillingRateForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/billing-rates/:id/edit"
            element={
              <ProtectedRoute requireAdmin>
                <BillingRateForm />
              </ProtectedRoute>
            }
          />

          <Route
            path="/user-rates"
            element={
              <ProtectedRoute requireAdmin>
                <UserRateManagement />
              </ProtectedRoute>
            }
          />

          <Route
            path="/rate-groups"
            element={
              <ProtectedRoute requireAdmin>
                <RateGroupList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/rate-groups/new"
            element={
              <ProtectedRoute requireAdmin>
                <RateGroupForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/rate-groups/:id/edit"
            element={
              <ProtectedRoute requireAdmin>
                <RateGroupForm />
              </ProtectedRoute>
            }
          />

          <Route
            path="/time-entry"
            element={
              <ProtectedRoute>
                <TimeEntry />
              </ProtectedRoute>
            }
          />

          <Route
            path="/my-timesheets"
            element={
              <ProtectedRoute>
                <MyTimesheets />
              </ProtectedRoute>
            }
          />

          <Route
            path="/timesheets/approvals"
            element={
              <ProtectedRoute requireAdmin>
                <TimesheetApprovalQueue />
              </ProtectedRoute>
            }
          />
          <Route
            path="/timesheets/report"
            element={
              <ProtectedRoute requireAdmin>
                <CustomerTimesheetReport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/unbilled-time"
            element={
              <ProtectedRoute requireAdmin>
                <UnbilledEntriesReport />
              </ProtectedRoute>
            }
          />

          <Route
            path="/reports/time"
            element={
              <ProtectedRoute requireAdmin>
                <InternalTimeReport />
              </ProtectedRoute>
            }
          />

          {/* Biweekly Payroll Routes */}
          <Route
            path="/pay-periods"
            element={
              <ProtectedRoute requireAdmin>
                <PayPeriodManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/biweekly"
            element={
              <ProtectedRoute>
                <BiweeklyTimesheet />
              </ProtectedRoute>
            }
          />
          <Route
            path="/biweekly-approvals"
            element={
              <ProtectedRoute requireAdmin>
                <BiweeklyApprovalQueue />
              </ProtectedRoute>
            }
          />

          <Route
            path="/billing-settings"
            element={
              <ProtectedRoute requireAdmin>
                <BillingSettings />
              </ProtectedRoute>
            }
          />
        </Routes>
      </div>
    </Router>
  );
};

export default App;
